import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Modal,
  SelectField,
  SkeletonRows,
  TextField,
  mutedTextClass,
} from '@/components/layout/ui'
import { ApiError, invitationsApi, membersApi, orgsApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { fieldErrorFor, isFormLevel, messageFor } from '@/lib/errors'
import { formatLimit, formatRelative, titleCase } from '@/lib/format'
import { useCopyToClipboard } from '@/lib/hooks'
import { ASSIGNABLE_ROLES, ROLE_DESCRIPTIONS } from '@/lib/permissions'
import { useBillingSummary, useInvitations, useMembers } from '@/lib/queries'
import { queryKeys } from '@/lib/queryClient'
import { useSession } from '@/lib/session'
import { useWorkspace } from '@/lib/workspace'
import type { Membership, Role } from '@/lib/types'

/**
 * `/{slug}/settings/members` — the roster, the seat meter and the invitation
 * queue.
 *
 * Every affordance here is mirrored server-side: the role select is hidden for
 * a viewer and the owner row is never editable, but the API is what actually
 * enforces it, and a 403 from it is shown verbatim rather than swallowed.
 *
 * The seat cap is the same story. The invite form is replaced by an upgrade
 * prompt once the workspace is full — a form that can only ever produce an
 * error is worse than no form — but the server refuses independently, and its
 * 402 is rendered inline if one arrives anyway (someone else took the last seat
 * while this page was open).
 */
export default function MembersPage() {
  const workspace = useWorkspace()
  const { user, refresh } = useSession()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const members = useMembers(workspace.slug)
  const canInvite = workspace.can('member.invite')
  const invitations = useInvitations(workspace.slug, canInvite && !workspace.isPersonal)
  // Every member may read the summary — only the owner may spend from it — so
  // the seat meter is visible to whoever is looking at the roster.
  const billing = useBillingSummary(workspace.slug, !workspace.isPersonal)

  const [pendingRemoval, setPendingRemoval] = useState<Membership | null>(null)
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferTo, setTransferTo] = useState<string>('')
  const { copied, copy } = useCopyToClipboard()

  const invalidateMembers = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.members(workspace.slug) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.billingSummary(workspace.slug) })
  }

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: Role }) =>
      membersApi.changeRole(workspace.slug, userId, role),
    onSuccess: invalidateMembers,
  })

  const removeMember = useMutation({
    mutationFn: (userId: number) => membersApi.remove(workspace.slug, userId),
    onSuccess: async (_result, userId) => {
      setPendingRemoval(null)
      if (userId === user?.id) {
        // Leaving: the workspace in the URL is no longer ours to render.
        queryClient.clear()
        await refresh()
        navigate('/', { replace: true })
        return
      }
      invalidateMembers()
    },
  })

  const transfer = useMutation({
    mutationFn: (userId: number) => orgsApi.transferOwnership(workspace.slug, userId),
    onSuccess: async () => {
      setTransferOpen(false)
      await refresh()
      invalidateMembers()
    },
  })

  if (workspace.isPersonal) {
    return (
      <Card>
        <CardHeader title="Members" />
        <EmptyState
          title="Personal workspaces are just you"
          description="Create a team workspace to invite people, assign issues and share a plan."
          action={
            <Button variant="primary" onClick={() => navigate('/new-workspace')}>
              New team workspace
            </Button>
          }
        />
      </Card>
    )
  }

  const roster = members.data?.data ?? []
  const owner = roster.find((member) => member.role === 'owner')
  const transferable = roster.filter((member) => member.role !== 'owner')

  // Seats: taken counts everyone who has joined PLUS everyone holding a live
  // invitation, because that is exactly what the server counts. A page that
  // showed only the roster would offer a form the API then refuses.
  const seatLimit = billing.data?.entitlements.limits.members ?? 0
  const planName =
    billing.data?.plans.find((plan) => plan.id === billing.data?.entitlements.plan)?.name ??
    titleCase(billing.data?.entitlements.plan ?? '')
  const pendingCount = (invitations.data ?? []).filter((invite) => invite.status === 'pending').length
  const seatsTaken = (members.data?.total ?? roster.length) + pendingCount
  const seatsFull = seatLimit > 0 && seatsTaken >= seatLimit
  const billingPath = workspace.path('/settings/billing')

  return (
    <div className="space-y-6">
      {canInvite ? (
        seatsFull ? (
          <SeatsExhausted planName={planName} limit={seatLimit} billingPath={billingPath} />
        ) : (
          <InviteForm slug={workspace.slug} billingPath={billingPath} />
        )
      ) : null}

      <Card>
        <CardHeader
          title="Members"
          description={
            members.data
              ? // The invitation list is admin-only, so a plain member cannot
                // see the pending half of the count. Rather than show them a
                // seat figure that is quietly too low, show the roster instead.
                (canInvite
                  ? `${seatsTaken} of ${formatLimit(seatLimit)} seats used` +
                    (pendingCount > 0 ? ` · ${members.data.total} joined, ${pendingCount} invited` : '')
                  : `${members.data.total} ${members.data.total === 1 ? 'person' : 'people'}`) +
                (billing.data ? ` · ${planName} plan` : '')
              : undefined
          }
          action={
            workspace.can('org.transfer') && transferable.length > 0 ? (
              <Button size="sm" onClick={() => setTransferOpen(true)}>
                Transfer ownership
              </Button>
            ) : null
          }
        />

        {changeRole.error ? (
          <div className="px-5 pt-4">
            <Alert tone="danger">{messageFor(changeRole.error)}</Alert>
          </div>
        ) : null}
        {removeMember.error ? (
          <div className="px-5 pt-4">
            <Alert tone="danger">{messageFor(removeMember.error)}</Alert>
          </div>
        ) : null}

        {members.isPending ? (
          <div className="p-5">
            <SkeletonRows rows={3} />
          </div>
        ) : members.isError ? (
          <div className="p-5">
            <Alert tone="danger">{messageFor(members.error)}</Alert>
          </div>
        ) : (
          <ul className="divide-y divide-ink-200 dark:divide-ink-800">
            {roster.map((member) => {
              const isSelf = member.user_id === user?.id
              const isOwner = member.role === 'owner'
              const editable = workspace.can('member.change_role') && !isOwner && !isSelf
              const removable = (workspace.can('member.remove') && !isOwner && !isSelf) || (isSelf && !isOwner)

              return (
                <li key={member.user_id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                  <Avatar
                    name={member.user?.name ?? `User ${member.user_id}`}
                    color={member.user?.avatar_color}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {member.user?.name ?? `User ${member.user_id}`}
                      {isSelf ? <span className={cn('ml-2 text-xs', mutedTextClass)}>You</span> : null}
                    </p>
                    <p className={cn('truncate text-xs', mutedTextClass)}>
                      {member.user?.email ?? '—'}
                      {member.joined_at ? ` · joined ${formatRelative(member.joined_at)}` : ''}
                    </p>
                  </div>

                  {editable ? (
                    <label className="sr-only" htmlFor={`role-${member.user_id}`}>
                      Role for {member.user?.name ?? member.user_id}
                    </label>
                  ) : null}
                  {editable ? (
                    <select
                      id={`role-${member.user_id}`}
                      value={member.role}
                      disabled={changeRole.isPending}
                      onChange={(event) =>
                        changeRole.mutate({
                          userId: member.user_id,
                          role: event.target.value as Role,
                        })
                      }
                      className="h-8 rounded-lg border border-ink-200 bg-white px-2 text-sm transition focus:border-brand-500 dark:border-ink-700 dark:bg-ink-950"
                    >
                      {ASSIGNABLE_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {titleCase(role)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Badge tone={isOwner ? 'brand' : 'neutral'}>{titleCase(member.role)}</Badge>
                  )}

                  {removable ? (
                    <Button size="sm" variant="ghost" onClick={() => setPendingRemoval(member)}>
                      {isSelf ? 'Leave' : 'Remove'}
                    </Button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      {canInvite ? (
        <Card>
          <CardHeader title="Pending invitations" description="Links are single-use and expire." />
          {invitations.isPending ? (
            <div className="p-5">
              <SkeletonRows rows={2} />
            </div>
          ) : invitations.isError ? (
            <div className="p-5">
              <Alert tone="danger">{messageFor(invitations.error)}</Alert>
            </div>
          ) : invitations.data.filter((invite) => invite.status === 'pending').length === 0 ? (
            <EmptyState title="No pending invitations" />
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {invitations.data
                .filter((invite) => invite.status === 'pending')
                .map((invite) => (
                  <InvitationRow
                    key={invite.id}
                    slug={workspace.slug}
                    id={invite.id}
                    email={invite.email}
                    role={invite.role}
                    expiresAt={invite.expires_at}
                    copiedKey={copied}
                    onCopy={copy}
                  />
                ))}
            </ul>
          )}
        </Card>
      ) : null}

      <ConfirmDialog
        open={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => pendingRemoval && removeMember.mutate(pendingRemoval.user_id)}
        title={
          pendingRemoval?.user_id === user?.id ? 'Leave this workspace?' : 'Remove this member?'
        }
        description={
          pendingRemoval?.user_id === user?.id
            ? 'You will lose access to its projects and issues until someone invites you back.'
            : `${pendingRemoval?.user?.name ?? 'They'} will lose access immediately.`
        }
        confirmLabel={pendingRemoval?.user_id === user?.id ? 'Leave' : 'Remove'}
        loading={removeMember.isPending}
        error={removeMember.error ? messageFor(removeMember.error) : null}
      />

      <Modal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        title="Transfer ownership"
        description={`${owner?.user?.name ?? 'The current owner'} becomes an admin. Only the owner can bill or delete a workspace.`}
        footer={
          <>
            <Button onClick={() => setTransferOpen(false)} disabled={transfer.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={transfer.isPending}
              disabled={!transferTo}
              onClick={() => transfer.mutate(Number(transferTo))}
            >
              Transfer
            </Button>
          </>
        }
      >
        <SelectField
          label="New owner"
          value={transferTo}
          onChange={(event) => setTransferTo(event.target.value)}
        >
          <option value="">Select a member…</option>
          {transferable.map((member) => (
            <option key={member.user_id} value={member.user_id}>
              {member.user?.name ?? `User ${member.user_id}`} ({member.user?.email})
            </option>
          ))}
        </SelectField>
        {transfer.error ? (
          <Alert tone="danger" className="mt-3">
            {messageFor(transfer.error)}
          </Alert>
        ) : null}
      </Modal>
    </div>
  )
}

/**
 * The workspace has no seats left. A dead invite form that always 402s is worse
 * than none, so it is replaced by the one action that fixes the problem.
 */
function SeatsExhausted({
  planName,
  limit,
  billingPath,
}: {
  planName: string
  limit: number
  billingPath: string
}) {
  const navigate = useNavigate()
  return (
    <Card>
      <CardHeader title="Invite people" />
      <EmptyState
        title={`Every seat on ${planName} is taken`}
        description={`This workspace includes ${limit} ${limit === 1 ? 'seat' : 'seats'}, counting both the people who have joined and the invitations nobody has accepted yet. Upgrade for more, or revoke a pending invitation to free one up.`}
        action={
          <Button variant="primary" onClick={() => navigate(billingPath)}>
            See plans
          </Button>
        }
      />
    </Card>
  )
}

/** Invite by email. The response carries the raw link once, for copy-and-paste. */
function InviteForm({ slug, billingPath }: { slug: string; billingPath: string }) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('member')
  const { copied, copy } = useCopyToClipboard()

  const invite = useMutation({
    mutationFn: () => invitationsApi.create(slug, { email: email.trim(), role }),
    onSuccess: () => {
      setEmail('')
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitations(slug) })
    },
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    invite.mutate()
  }

  const link = invite.data?.invite_url

  return (
    <Card>
      <CardHeader
        title="Invite people"
        description="They receive a single-use link that expires in seven days."
      />
      <form onSubmit={handleSubmit} className="space-y-4 p-5" noValidate>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="flex-1">
            <TextField
              label="Email address"
              type="email"
              autoComplete="off"
              placeholder="teammate@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={fieldErrorFor(invite.error, 'email')}
            />
          </div>
          <div className="sm:w-44">
            <SelectField
              label="Role"
              value={role}
              hint={ROLE_DESCRIPTIONS[role]}
              onChange={(event) => setRole(event.target.value as Role)}
              error={fieldErrorFor(invite.error, 'role')}
            >
              {ASSIGNABLE_ROLES.map((option) => (
                <option key={option} value={option}>
                  {titleCase(option)}
                </option>
              ))}
            </SelectField>
          </div>
        </div>

        {invite.error instanceof ApiError && invite.error.isPlanLimit ? (
          // The last seat went while this form was open. The server's sentence
          // already names the plan and the cap, so it is shown verbatim.
          <Alert tone="warning" title="No seats left">
            <p>{invite.error.message}</p>
            <p className="mt-2">
              <Link to={billingPath} className="font-medium underline underline-offset-2">
                See plans
              </Link>
            </p>
          </Alert>
        ) : invite.error && isFormLevel(invite.error, ['email', 'role']) ? (
          <Alert tone="danger">{messageFor(invite.error)}</Alert>
        ) : null}

        {invite.isSuccess && link ? (
          <Alert tone="success" title={`Invitation sent to ${invite.data.email}`}>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-white/70 px-2 py-1 font-mono text-[11px] dark:bg-ink-950/50">
                {link}
              </code>
              <Button size="sm" onClick={() => void copy(link)}>
                {copied === link ? 'Copied' : 'Copy link'}
              </Button>
            </div>
          </Alert>
        ) : null}

        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={invite.isPending} disabled={!email.trim()}>
            Send invitation
          </Button>
        </div>
      </form>
    </Card>
  )
}

function InvitationRow({
  slug,
  id,
  email,
  role,
  expiresAt,
  copiedKey,
  onCopy,
}: {
  slug: string
  id: string
  email: string
  role: Role
  expiresAt: string
  copiedKey: string | null
  onCopy: (text: string, key?: string) => Promise<boolean>
}) {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.invitations(slug) })

  const resend = useMutation({
    mutationFn: () => invitationsApi.resend(slug, id),
    onSuccess: invalidate,
  })
  const revoke = useMutation({
    mutationFn: () => invitationsApi.revoke(slug, id),
    onSuccess: invalidate,
  })

  const freshLink = resend.data?.invite_url

  return (
    <li className="flex flex-wrap items-center gap-3 px-5 py-3.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{email}</p>
        <p className={cn('truncate text-xs', mutedTextClass)}>
          {titleCase(role)} · expires {formatRelative(expiresAt)}
        </p>
        {freshLink ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-ink-100 px-2 py-1 font-mono text-[11px] dark:bg-ink-800">
              {freshLink}
            </code>
            <Button size="sm" onClick={() => void onCopy(freshLink)}>
              {copiedKey === freshLink ? 'Copied' : 'Copy'}
            </Button>
          </div>
        ) : null}
        {resend.error ? (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{messageFor(resend.error)}</p>
        ) : null}
        {revoke.error ? (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{messageFor(revoke.error)}</p>
        ) : null}
      </div>
      <Button size="sm" loading={resend.isPending} onClick={() => resend.mutate()}>
        Resend
      </Button>
      <Button size="sm" variant="ghost" loading={revoke.isPending} onClick={() => revoke.mutate()}>
        Revoke
      </Button>
    </li>
  )
}
